'use client';

import React, { ReactNode, useEffect, useLayoutEffect, useRef } from 'react';

import { cn } from '@/lib/utils';

import Section, {
  DataTrackerSectionProps,
} from '@/components/atoms/Section/Section';

import {
  useMarqueeAnimation,
  UseMarqueeAnimationParams,
} from './useMarqueeAnimation';

export type MarqueeProps = {
  config?: UseMarqueeAnimationParams;
  gap?: number;
  syncSeamWithItemGap?: boolean;
  repeat: number;
  children: ReactNode;
  classNames?: {
    root?: string;
    inner?: string;
  };
} & DataTrackerSectionProps;

const KEYFRAME_NAME = 'marquee-scroll';

function measureItemGap(segment: HTMLElement): number {
  const items = Array.from(segment.children) as HTMLElement[];
  if (items.length < 2) return 0;

  const first = items[0];
  const second = items[1];
  return Math.max(0, second.offsetLeft - first.offsetLeft - first.offsetWidth);
}

export const Marquee = React.memo(function Marquee({
  children,
  classNames,
  gap = 0,
  syncSeamWithItemGap = false,
  repeat,
  config = {},
  ...props
}: MarqueeProps) {
  const { duration, isPaused, playMarquee, pauseMarquee } =
    useMarqueeAnimation(config);
  const segmentRefs = useRef<(HTMLDivElement | null)[]>([]);

  const onMouseEnter = () => pauseMarquee();
  const onMouseLeave = () => playMarquee();

  useEffect(() => {
    if (
      typeof window !== 'undefined' &&
      !document.getElementById('marquee-keyframes')
    ) {
      const style = document.createElement('style');
      style.id = 'marquee-keyframes';
      style.textContent = `
        @keyframes ${KEYFRAME_NAME} {
          from {
            transform: translateX(0);
          }
          to {
            transform: translateX(-100%);
          }
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  useLayoutEffect(() => {
    if (!syncSeamWithItemGap) return;

    const segment = segmentRefs.current[0];
    if (!segment) return;

    const applySeamGap = () => {
      const nextGap = measureItemGap(segment);
      segmentRefs.current.forEach((el) => {
        if (el) el.style.paddingRight = `${nextGap}px`;
      });
    };

    applySeamGap();

    const resizeObserver = new ResizeObserver(applySeamGap);
    resizeObserver.observe(segment);

    const images = segment.querySelectorAll('img');
    images.forEach((img) => img.addEventListener('load', applySeamGap));
    window.addEventListener('resize', applySeamGap);

    return () => {
      resizeObserver.disconnect();
      images.forEach((img) => img.removeEventListener('load', applySeamGap));
      window.removeEventListener('resize', applySeamGap);
    };
  }, [syncSeamWithItemGap, children, repeat]);

  return (
    <Section
      className={cn('flex w-full overflow-hidden', classNames?.root)}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      data-tracker-section={props['data-tracker-section']}
    >
      {Array.from({ length: repeat }, (_, i) => (
        <div
          key={i}
          ref={(el) => {
            segmentRefs.current[i] = el;
          }}
          className={cn(
            'flex shrink-0',
            syncSeamWithItemGap && 'box-content w-full',
            classNames?.inner
          )}
          aria-hidden={i > 0}
          style={{
            paddingRight: syncSeamWithItemGap ? undefined : `${gap}px`,
            animation: `${KEYFRAME_NAME} ${duration}s linear infinite`,
            animationPlayState: isPaused ? 'paused' : 'running',
          }}
        >
          {children}
        </div>
      ))}
    </Section>
  );
});
